import { useEffect, useState } from "react";
import { api } from "../api.js";

interface Benutzer {
  id: string;
  email: string;
  name: string;
  role: "admin" | "user";
  active: number;
  created_at: number;
  last_login_at: number | null;
  locked_until: number | null;
  wartet_auf_einloesung: number;
  offene_einladungen: number;
}

const datum = (ms: number | null) =>
  ms ? new Date(ms).toLocaleString("de-DE", { timeZone: "Europe/Berlin" }) : "—";

export function BenutzerVerwaltung({ eigeneId }: { eigeneId: string }) {
  const [liste, setListe] = useState<Benutzer[] | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [erfolg, setErfolg] = useState<string | null>(null);
  const [link, setLink] = useState<{ url: string; name: string; zweck: string } | null>(null);
  const [neuOffen, setNeuOffen] = useState(false);

  function laden() {
    api
      .get<Benutzer[]>("/verwaltung/benutzer")
      .then(setListe)
      .catch((e) => setFehler(e.message));
  }
  useEffect(laden, []);

  /**
   * Der Server liefert nur den Token. Den Link baut der Client aus der
   * eigenen Herkunft — dann stellt sich die Frage nach einem
   * vertrauenswürdigen Host-Header gar nicht erst.
   *
   * Der Token steht im FRAGMENT: das überträgt der Browser nie an den
   * Server, taucht also in keinem Zugriffsprotokoll auf.
   */
  const baueLink = (token: string) => `${location.origin}/einladung#${token}`;

  async function anlegen(e: React.FormEvent, f: { email: string; name: string; role: string }) {
    e.preventDefault();
    setFehler(null);
    try {
      const a = await api.post<{ token: string; name: string }>("/verwaltung/benutzer", f);
      setLink({ url: baueLink(a.token), name: a.name, zweck: "einladung" });
      setNeuOffen(false);
      setErfolg(null);
      laden();
    } catch (err) {
      setFehler(err instanceof Error ? err.message : "Anlegen fehlgeschlagen.");
    }
  }

  async function aendern(b: Benutzer, teil: { role?: string; active?: boolean }) {
    setFehler(null);
    try {
      await api.patch(`/verwaltung/benutzer/${b.id}`, teil);
      setErfolg(`${b.name} wurde geändert.`);
      laden();
    } catch (err) {
      setFehler(err instanceof Error ? err.message : "Änderung fehlgeschlagen.");
    }
  }

  async function zuruecksetzen(b: Benutzer) {
    setFehler(null);
    try {
      const a = await api.post<{ token: string }>(`/verwaltung/benutzer/${b.id}/reset`, {});
      setLink({ url: baueLink(a.token), name: b.name, zweck: "reset" });
      laden();
    } catch (err) {
      setFehler(err instanceof Error ? err.message : "Zurücksetzen fehlgeschlagen.");
    }
  }

  if (fehler && !liste)
    return (
      <div className="meldung fehler" role="alert">
        {fehler}
      </div>
    );
  if (!liste) return <p className="leer">Benutzer werden geladen …</p>;

  return (
    <>
      {fehler && (
        <div className="meldung fehler" role="alert">
          {fehler}
        </div>
      )}
      {erfolg && (
        <div className="meldung erfolg" role="status">
          {erfolg}
        </div>
      )}

      {link && <LinkKarte link={link} beiSchliessen={() => setLink(null)} />}

      {neuOffen ? (
        <NeuerBenutzer beiAnlegen={anlegen} beiAbbruch={() => setNeuOffen(false)} />
      ) : (
        <div className="knopfzeile" style={{ marginBottom: "1.25rem" }}>
          <button className="knopf" onClick={() => setNeuOffen(true)}>
            + Neuer Benutzer
          </button>
        </div>
      )}

      <section className="karte">
        <h2>Benutzer</h2>
        <div className="tabelle-scroll">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>E-Mail</th>
                <th>Rolle</th>
                <th>Status</th>
                <th>Letzte Anmeldung</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {liste.map((b) => (
                <tr key={b.id} style={{ opacity: b.active ? 1 : 0.55 }}>
                  <td style={{ fontWeight: 600 }}>
                    {b.name}
                    {b.id === eigeneId && (
                      <span style={{ color: "var(--faded)", fontWeight: 400 }}> (Sie)</span>
                    )}
                  </td>
                  <td style={{ fontSize: "0.88em" }}>{b.email}</td>
                  <td>
                    <select
                      value={b.role}
                      onChange={(e) => aendern(b, { role: e.target.value })}
                      style={{ width: "auto" }}
                    >
                      <option value="user">Sachbearbeiter</option>
                      <option value="admin">Administrator</option>
                    </select>
                  </td>
                  <td>
                    {!b.active ? (
                      <span className="abzeichen abgelehnt">deaktiviert</span>
                    ) : b.wartet_auf_einloesung && b.offene_einladungen > 0 ? (
                      <span className="abzeichen abgelaufen">Einladung offen</span>
                    ) : b.wartet_auf_einloesung ? (
                      /* Kein gültiger Link mehr — sonst sähe der Admin nicht,
                         dass der Kollege festhängt. */
                      <span className="abzeichen abgelehnt">Link abgelaufen</span>
                    ) : b.locked_until && b.locked_until > Date.now() ? (
                      <span className="abzeichen abgelaufen">gesperrt</span>
                    ) : (
                      <span className="abzeichen angenommen">aktiv</span>
                    )}
                  </td>
                  <td style={{ fontSize: "0.85em", color: "var(--muted)" }}>
                    {datum(b.last_login_at)}
                  </td>
                  <td>
                    <div className="knopfzeile">
                      <button
                        className="knopf sekundaer"
                        onClick={() => aendern(b, { active: !b.active })}
                      >
                        {b.active ? "Deaktivieren" : "Aktivieren"}
                      </button>
                      {b.active && b.id !== eigeneId && (
                        <button className="knopf schlicht" onClick={() => zuruecksetzen(b)}>
                          {b.wartet_auf_einloesung ? "Neuer Link" : "Passwort zurücksetzen"}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}

function LinkKarte({
  link,
  beiSchliessen,
}: {
  link: { url: string; name: string; zweck: string };
  beiSchliessen: () => void;
}) {
  const [kopiert, setKopiert] = useState(false);
  const reset = link.zweck === "reset";

  return (
    <section className="karte" style={{ borderLeft: "3px solid var(--engelke-blau)" }}>
      <h2>{reset ? "Link zum Zurücksetzen" : "Einladungslink"} für {link.name}</h2>

      <div className="meldung hinweis">
        <strong>Dieser Link wird nur jetzt angezeigt.</strong> Der Server speichert nur seine
        Prüfsumme und kann ihn nicht erneut herausgeben. Bitte{" "}
        <strong>persönlich oder telefonisch übergeben</strong> — nicht per E-Mail oder Messenger,
        das sind die üblichen Abgriffspunkte für solche Links.
        {reset ? " Gültig für eine Stunde." : " Gültig für sieben Tage."}
      </div>

      <div className="linkfeld">
        <input readOnly value={link.url} onFocus={(e) => e.currentTarget.select()} />
        <button
          className="knopf"
          onClick={async () => {
            await navigator.clipboard.writeText(link.url).catch(() => {});
            setKopiert(true);
          }}
        >
          {kopiert ? "Kopiert" : "Kopieren"}
        </button>
      </div>

      <div className="knopfzeile" style={{ marginTop: "0.9rem" }}>
        <button className="knopf sekundaer" onClick={beiSchliessen}>
          Schließen
        </button>
      </div>
    </section>
  );
}

function NeuerBenutzer({
  beiAnlegen,
  beiAbbruch,
}: {
  beiAnlegen: (e: React.FormEvent, f: { email: string; name: string; role: string }) => void;
  beiAbbruch: () => void;
}) {
  const [f, setF] = useState({ email: "", name: "", role: "user" });

  return (
    <section className="karte">
      <h2>Neuer Benutzer</h2>
      <p className="hinweistext" style={{ marginTop: 0 }}>
        Sie vergeben kein Passwort. Der neue Benutzer bekommt einen Einladungslink und wählt es
        selbst — die Passwortstreckung läuft im Browser, ein Passwort könnte hier gar nicht
        gesetzt werden, ohne es im Klartext zu kennen.
      </p>
      <form onSubmit={(e) => beiAnlegen(e, f)}>
        <div className="raster">
          <div className="feld">
            <label htmlFor="b-name">Name</label>
            <input
              id="b-name"
              required
              autoFocus
              value={f.name}
              onChange={(e) => setF({ ...f, name: e.target.value })}
            />
          </div>
          <div className="feld">
            <label htmlFor="b-mail">E-Mail</label>
            <input
              id="b-mail"
              type="email"
              required
              value={f.email}
              onChange={(e) => setF({ ...f, email: e.target.value })}
            />
          </div>
          <div className="feld">
            <label htmlFor="b-rolle">Rolle</label>
            <select
              id="b-rolle"
              value={f.role}
              onChange={(e) => setF({ ...f, role: e.target.value })}
            >
              <option value="user">Sachbearbeiter</option>
              <option value="admin">Administrator</option>
            </select>
          </div>
        </div>
        <div className="knopfzeile">
          <button className="knopf" type="submit">
            Anlegen und Link erzeugen
          </button>
          <button type="button" className="knopf sekundaer" onClick={beiAbbruch}>
            Abbrechen
          </button>
        </div>
      </form>
    </section>
  );
}
